import { Input } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { AxisValueDTO } from '@/services/classification.service'

/**
 * Every value on an axis, in one list.
 *
 * ONE component for hierarchical and flat axes alike, because the difference between them
 * is data rather than layout: a hierarchical axis nests, so its rows indent, and that is
 * all. Two components meant the two kinds of axis looked and behaved differently for no
 * reason a reader could name.
 *
 * A row LINKS to the value rather than editing in place. A value carries per-market names,
 * availability and external codes — more than a row can hold — so editing lives on its own
 * page and this stays scannable.
 */

interface Node extends AxisValueDTO {
	depth: number
}

/** Depth-first, parents before children, so the rendered order matches the hierarchy. */
function flatten(values: AxisValueDTO[]): Node[] {
	const childrenOf = new Map<string, AxisValueDTO[]>()
	const roots: AxisValueDTO[] = []
	const known = new Set(values.map((v) => v.valueId))
	for (const value of values) {
		// A parent outside the set (retired, or filtered out) makes its child a root here —
		// otherwise the child would vanish from a list that is meant to show everything.
		if (value.parentId && known.has(value.parentId)) {
			childrenOf.set(value.parentId, [...(childrenOf.get(value.parentId) ?? []), value])
		} else {
			roots.push(value)
		}
	}
	const byLabel = (a: AxisValueDTO, b: AxisValueDTO) =>
		a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
	const out: Node[] = []
	const visit = (value: AxisValueDTO, depth: number) => {
		out.push({ ...value, depth })
		for (const child of [...(childrenOf.get(value.valueId) ?? [])].sort(byLabel)) visit(child, depth + 1)
	}
	for (const root of [...roots].sort(byLabel)) visit(root, 0)
	return out
}

/** A search matches a value's label OR its code — people quote both. */
const matches = (value: AxisValueDTO, needle: string): boolean =>
	value.label.toLowerCase().includes(needle) || value.code.toLowerCase().includes(needle)

export interface AxisValueListProps {
	axisId: string
	values: AxisValueDTO[]
	hierarchical: boolean
	onRetire: (valueId: string) => void
	onMerge: (valueId: string) => void
}

/** How many values before the list offers a filter. */
const FILTER_THRESHOLD = 12

export function AxisValueList({ axisId, values, hierarchical, onRetire, onMerge }: AxisValueListProps) {
	const [query, setQuery] = useState('')

	const rows = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (!needle) return flatten(values)
		// Filtering flattens the hierarchy on purpose: a match whose parent does not match
		// would otherwise render at a depth with nothing above it, reading as a broken tree.
		return values.filter((v) => matches(v, needle)).map((v) => ({ ...v, depth: 0 }))
	}, [values, query])

	return (
		<div className="space-y-3">
			{values.length >= FILTER_THRESHOLD && (
				<div className="flex items-center gap-3">
					<Input
						className="max-w-xs"
						placeholder="Filter values…"
						aria-label="Filter values"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
					/>
					<span className="text-ds-text-muted text-sm">
						{rows.length}
						{rows.length !== values.length ? ` / ${values.length}` : ''} values
					</span>
				</div>
			)}

			{rows.length === 0 ? (
				<p className="text-ds-text-muted text-sm">{values.length === 0 ? 'No values yet.' : 'No matches.'}</p>
			) : (
				<ul className="divide-y divide-ds-border">
					{rows.map((value) => {
						const retired = value.status === 'retired' || value.validTo !== null
						return (
							<li key={value.valueId} className="flex items-center justify-between gap-4 py-2">
								<div
									className="min-w-0 flex-1"
									// Indent by hierarchy depth. Inline because the depth is data — a
									// class per level would cap how deep an axis may go.
									style={hierarchical ? { paddingLeft: `${value.depth * 1.25}rem` } : undefined}
								>
									<Link
										to={`/admin/classification/axes/${axisId}/values/${value.valueId}`}
										className="text-ds-text text-sm hover:underline"
									>
										<span className={retired ? 'text-ds-text-subtle line-through' : 'font-medium'}>{value.label}</span>
									</Link>
									<span className="ml-2 font-mono text-ds-text-subtle text-xs">{value.code}</span>
									{retired && <span className="ml-2 text-ds-text-subtle text-xs">retired</span>}
								</div>
								<div className="flex shrink-0 gap-3 text-xs">
									{!retired && (
										<>
											<button
												type="button"
												className="text-ds-info hover:underline"
												onClick={() => onMerge(value.valueId)}
											>
												merge
											</button>
											<button
												type="button"
												className="text-ds-danger hover:underline"
												onClick={() => onRetire(value.valueId)}
											>
												retire
											</button>
										</>
									)}
								</div>
							</li>
						)
					})}
				</ul>
			)}
		</div>
	)
}
